import {
    ITrialCalendarSlot,
    ITrialLesson,
    TTrialLessonStatus,
} from "../../../entities/trialLesson";
import { getCalendarDayEventLayout } from "../../../shared/lib/helpers/calendarDayEventLayout";
import { getTrialVisibleRange } from "./date";
import {
    IDisplayedPositionedTrialLesson,
    IGroupedTrialLessons,
    IPositionedTrialLesson,
} from "./types";

const TRIAL_LESSON_STATUS_ORDER: readonly TTrialLessonStatus[] = [
    'TRIAL_LESSON_STATUS_ASSIGNED',
    'TRIAL_LESSON_STATUS_ORANGE_PENDING',
    'TRIAL_LESSON_STATUS_COMPLETED',
    'TRIAL_LESSON_STATUS_STUDENT_CREATED',
    'TRIAL_LESSON_STATUS_LOST',
    'TRIAL_LESSON_STATUS_UNSPECIFIED',
]

export const getTrialCalendarSlotsByIndex = (
    slots: ITrialCalendarSlot[],
    date: Date
): Map<number, ITrialCalendarSlot> => {
    const result = new Map<number, ITrialCalendarSlot>()

    slots.forEach(slot => {
        const visibleRange = getTrialVisibleRange(date, slot.startsAt, slot.durationSlots)

        if(!visibleRange) return

        for(let index = visibleRange.startIndex; index < visibleRange.endIndex; index++){
            const currentSlot = result.get(index)

            result.set(index, currentSlot
                ? {
                    ...currentSlot,
                    availableActions: currentSlot.availableActions.length ? currentSlot.availableActions : slot.availableActions,
                    trialLessons: [
                        ...currentSlot.trialLessons,
                        ...slot.trialLessons.filter(trialLesson => (
                            !currentSlot.trialLessons.some(currentTrialLesson => currentTrialLesson.id === trialLesson.id)
                        )),
                    ],
                }
                : slot
            )
        }
    })

    return result
}

export const getPositionedTrialLessons = (
    slots: ITrialCalendarSlot[],
    date: Date
): IPositionedTrialLesson[] => {
    const lessonsById = new Map<string, ITrialLesson>()

    slots.forEach(slot => {
        slot.trialLessons.forEach(trialLesson => lessonsById.set(trialLesson.id, trialLesson))
    })

    return [...lessonsById.values()]
        .map(trialLesson => {
            const visibleRange = getTrialVisibleRange(date, trialLesson.startsAt, trialLesson.durationSlots)

            return visibleRange ? {trialLesson, ...visibleRange} : null
        })
        .filter((item): item is IPositionedTrialLesson => Boolean(item))
        .sort((first, second) => (
            first.startIndex - second.startIndex ||
            second.endIndex - first.endIndex
        ))
}

export const getTrialCalendarLessonLayout = (
    positionedLessons: IPositionedTrialLesson[],
    groupSingleLessons = false,
): {
    visibleLessons: IDisplayedPositionedTrialLesson[];
    groupedLessons: IGroupedTrialLessons[];
} => {
    const {visibleEvents, groupedEvents} = getCalendarDayEventLayout(
        positionedLessons,
        TRIAL_LESSON_STATUS_ORDER,
        positionedLesson => positionedLesson.trialLesson.status,
        groupSingleLessons,
    )

    return {
        visibleLessons: visibleEvents,
        groupedLessons: groupedEvents,
    }
}
